<template>
    <Spin :spinning="isLoading">
        <SearchPanel
            v-if="visibleSearch"
            :dataTable="data"
            :propsTable="props"
            :columns="columnSearch"
            :ref="(e) => (refs.search = e)"
            @search="() => onClickSearch({ props, data, refs, emit })"
        ></SearchPanel>
        <OperatorAndPagination
            class="mt-2"
            type="top"
            v-if="manages.length > 0 || manageExtras.length > 0 || visiblePagination"
            :dataTable="data"
            :propsTable="props"
            :manages="manages"
            :manageExtras="manageExtras"
            :pagination="pagination"
            :canRefresh="canRefresh"
            :ref="(e) => (refs.operator1 = e)"
            @refresh="() => onClickSearch({ props, data, refs, emit })"
        ></OperatorAndPagination>
        <Row v-bind="rowProp">
            <Col v-for="record in pageComputed.data" :key="rowKey(record)" v-bind="colProp">
                <Card class="m-2" :bodyStyle="{ padding: '0px' }" :class="rowClassName(record)">
                    <div class="text-right mr-1">
                        <slot name="action" v-bind="{ record }"></slot>
                        <template v-if="data.rowSelection">
                            <Checkbox
                                :checked="data.rowSelection.selectedRowKeys.includes(rowKey(record))"
                                @change="(e) => data.rowSelection.onChangeCustom({ checked: e.target.checked, record, rowKey })"
                                v-bind="data.rowSelection.getCheckboxProps(record)"
                            ></Checkbox>
                        </template>
                    </div>
                    <slot name="item" v-bind="{ record }"></slot>
                </Card>
            </Col>
        </Row>
        <OperatorAndPagination
            v-if="(manages.length > 0 || manageExtras.length > 0 || visiblePagination) && pageComputed.data.length > 5"
            type="bottom"
            :dataTable="data"
            :propsTable="props"
            :manages="manages"
            :manageExtras="manageExtras"
            :pagination="pagination"
            :ref="(e) => (refs.operator2 = e)"
        ></OperatorAndPagination>
    </Spin>
</template>

<script>
import { Card, Checkbox, Col, Row, Spin } from "ant-design-vue";
import { computed, defineAsyncComponent, defineComponent, onMounted, reactive, watch } from "vue";
import { randomUuid } from "../../utils/util";
import {
    createColumnDisplay,
    createColumnSearch,
    createPage,
    createPagination,
    createTotalWidth,
    init,
    initRowSelection,
    onChangeTable,
    onClickSearch,
    rowKey,
} from "../table/c-table.logic";

export default defineComponent({
    components: {
        Spin,
        Card,
        Row,
        Col,
        Checkbox,
        OperatorAndPagination: defineAsyncComponent(() => import("../table/OperatorAndPagination")),
        SearchPanel: defineAsyncComponent(() => import("../table/SearchPanel")),
    },
    props: {
        columns: { default: () => [] },
        page: {},
        rowKey: { default: () => rowKey },
        size: { default: "small" },
        //pageSizeOptions: { default: () => ["10", "20", "50", "100"] },
        pageSizeOptions: { default: () => ["1", "2", "3", "4"] },
        sortDirections: { default: () => ["ascend", "descend"] },
        manages: { default: () => [] },
        manageExtras: { default: () => [] },
        rowActions: { default: () => [] },
        isLoading: { default: false },
        canRefresh: { default: true },
        clientSide: { default: false },
        childrenColumnName: { default: "" },
        rowClassName: { default: () => (record) => record.isActive === false ? "bg-inactive" : "" },
        visibleSearch: { default: true },
        visiblePagination: { default: true },
        visibleRowActions: { default: true },
        visibleItem: { default: () => () => true },
        searchFirst: { default: true },
        options: { default: () => ({ search: { activeKey: null } }) },
        rowProp: {},
        colProp: { default: { xs: { span: 24 }, sm: { span: 12 }, md: { span: 8 }, xl: { span: 6 } } },
    },
    emits: ["search", "update:page", "update:columns"],
    setup(props, { emit }) {
        const data = reactive({ tableKey: randomUuid(), columns: null, page: null, manageSelect: null, rowSelection: null, options: null });
        const refs = reactive({ search: null, card: null, operator1: null, operator2: null });
        init({ props, data, refs, emit });
        const pageComputed = computed(() => createPage({ props, data, refs, emit }));
        const columnDisplay = computed(() => createColumnDisplay({ props, data, refs, emit }));
        const columnSearch = computed(() => createColumnSearch({ props, data, refs, emit }));
        const totalWidth = computed(() => createTotalWidth({ props, data, refs, emit, columns: columnDisplay.value }));
        const pagination = computed(() => createPagination({ props, data, refs, emit, page: pageComputed.value }));
        data.manageSelect = props.manages?.find((manage) => manage.visible?.() && manage.force);
        if (data.manageSelect) {
            initRowSelection({ props, data, refs });
        }
        if (props.searchFirst) {
            onMounted(() => {
                const interval = setInterval(() => {
                    if (refs.search) {
                        onClickSearch({ props, data, refs, emit });
                        clearInterval(interval);
                    }
                }, 10);
            });
        }
        watch(
            () => ({
                page: {
                    page: props.page?.page,
                    size: props.page?.size,
                    totalPage: props.page?.totalPage,
                    totalSize: props.page?.totalSize,
                    dataLength: props.page?.data?.length,
                },
                columns: props.columns,
            }),
            () => init({ props, data, refs, emit }),
            { flush: "post", deep: true }
        );
        return {
            props,
            data,
            refs,
            emit,
            pageComputed,
            columnDisplay,
            columnSearch,
            totalWidth,
            pagination,
            onClickSearch,
            onChangeTable,
        };
    },
});
</script>
